import type { ReactNode } from "react";
import type { RouteName, User } from "../types";
import { Icon, type IconName } from "./Icon";

interface NavigationItem {
  route: RouteName;
  label: string;
  icon: IconName;
}

const userNavigation: NavigationItem[] = [
  { route: "dashboard", label: "Pulse", icon: "pulse" },
  { route: "plan", label: "Plan", icon: "calendar" },
  { route: "nutrition", label: "Fuel", icon: "leaf" },
  { route: "recovery", label: "Recovery", icon: "heart" },
  { route: "history", label: "History", icon: "history" },
  { route: "settings", label: "Settings", icon: "settings" },
];

interface AppShellProps {
  user: User;
  route: RouteName;
  connected: boolean;
  error: string;
  children: ReactNode;
  onRoute: (route: RouteName) => void;
  onSignOut: () => void;
  onDismissError: () => void;
}

export function AppShell({
  user,
  route,
  connected,
  error,
  children,
  onRoute,
  onSignOut,
  onDismissError,
}: AppShellProps) {
  const navigation = user.is_admin
    ? [...userNavigation, { route: "admin" as const, label: "Admin", icon: "admin" as const }]
    : userNavigation;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <button className="logo button-reset" type="button" onClick={() => onRoute("dashboard")}>
          <span>FT</span><strong>FitTracker</strong>
        </button>
        <nav aria-label="Main navigation">
          {navigation.map((item) => (
            <button
              key={item.route}
              className={`nav-link button-reset ${route === item.route ? "active" : ""}`}
              type="button"
              onClick={() => onRoute(item.route)}
            >
              <Icon name={item.icon} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sync-status">
            <span className={`signal-dot ${connected ? "" : "offline"}`} />
            <span>{connected ? "Live sync connected" : "Reconnecting"}</span>
          </div>
          <button className="button secondary" type="button" onClick={onSignOut}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      </aside>

      <header className="mobile-header">
        <button className="logo button-reset" type="button" onClick={() => onRoute("dashboard")}>
          <span>FT</span><strong>FitTracker</strong>
        </button>
        <button className="icon-button" type="button" onClick={() => onRoute("settings")} aria-label="Open settings">
          <Icon name="settings" />
        </button>
      </header>

      <main className="content-shell">
        {error ? (
          <div className="error-banner" role="alert">
            <span>{error}</span>
            <button className="icon-button" type="button" onClick={onDismissError} aria-label="Dismiss error">
              <Icon name="close" />
            </button>
          </div>
        ) : null}
        {children}
      </main>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        {userNavigation.slice(0, 5).map((item) => (
          <button
            key={item.route}
            className={`nav-link button-reset ${route === item.route ? "active" : ""}`}
            type="button"
            onClick={() => onRoute(item.route)}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
